import { matchPath } from 'react-router';
import { RoleHelper } from '@shoflo/polymer/helpers/RoleHelper/RoleHelper';
import Paths from 'AppPaths';
import _ from 'underscore';
import queryString from 'query-string';
import {
  IMAGE_FILE,
  VIDEO_FILE,
  AUDIO_FILE,
  PDF_FILE
} from '@shoflo/polymer/constants/SourceItems';
import { StudioLogger } from '@studio/logger/StudioLogger';
import { getMinutesDifference } from 'utils/utils';
import { SIGNED_URLS_CACHE_TIMEOUT_VALUE } from '../constants/appConstants';
import fileTypes from '@shoflo/studio-redux/constants/FileTypes';
import { uploadToPreSignedURL } from '@shoflo/studio-redux/utils/actionUtils';
import { readFileAsBinary } from '@shoflo/studio-redux/utils/sources';
import { STOCK_MEDIA } from '@studio/constants/Sources';

const logger = new StudioLogger('actions-utils');

const s3SourceTypes = [IMAGE_FILE, VIDEO_FILE, AUDIO_FILE, PDF_FILE];

export function getPathname(getState) {
  const { router } = getState();

  if (!router) return '';

  return router.location.pathname;
}

export function getEventRouteMatch(getState) {
  const pathName = getPathname(getState);

  let match = matchPath(pathName, {
    path: Paths.EventPage
  });

  return match;
}

export function getBroadcastRouteMatch(getState) {
  const pathName = getPathname(getState);

  let match = matchPath(pathName, {
    path: Paths.BroadcastPage
  });

  return match;
}

export const hasEventRouteState = getState => {
  const match = getEventRouteMatch(getState);

  return match ? true : false;
};

export const getCurrentTeamId = getState => {
  const teamRouteMatch = getEventRouteMatch(getState);
  if (!teamRouteMatch) return null;
  return teamRouteMatch.params.teamId;
};

export const getCurrentUserId = getState => {
  const { user } = getState();
  return user._id;
};

export const isAdminForTeam = getState => {
  const { user } = getState();
  const teamId = getCurrentTeamId(getState);
  return RoleHelper.hasTeamAdminRole(user, teamId);
};

/**
 * Helper for executing with current params..
 *
 * @param {*} cb
 */
export const withCurrentParams = cb => {
  return (dispatch, getState, socket) => {
    const match =
      getBroadcastRouteMatch(getState) || getEventRouteMatch(getState);

    if (match) {
      cb(dispatch, match.params, getState, socket);
    } else {
      throw new Error(
        'The current route does not have the necessary parameters'
      );
    }
  };
};

//TODO: move this somewhere else, maybe doesn't belong here is if it is not used in actions (maybe store/utils)
export function setAccessTokenFromQueryString(window, document) {
  const { accessToken } = queryString.parse(window.location.search);
  if (!accessToken) return;
  const encodedAccessToken = window.btoa(accessToken);
  document.cookie = `sf_access_token=${encodedAccessToken}; Path=/`;
}

let signedURLsCache = {
  lastUpdated: new Date().getTime()
};

export const addSignedUrlsToBroadcast = async (
  broadcast,
  apiClient,
  teamId,
  eventId
) => {
  const { overlays, backgrounds, globalSceneLayers, program } = broadcast;

  const modifiedOverlays = await mapSignedUrlsToSources(
    overlays,
    apiClient,
    teamId,
    eventId
  );
  const modifiedBackgrounds = await mapSignedUrlsToSources(
    backgrounds,
    apiClient,
    teamId,
    eventId
  );
  const modifiedGlobalSceneLayers = await mapSignedUrlsToSources(
    globalSceneLayers,
    apiClient,
    teamId,
    eventId
  );

  if (program?.layers) {
    const modifiedMediaLayers = await mapSignedUrlsToSources(
      program.layers,
      apiClient,
      teamId,
      eventId
    );
    const updatedProgram = {
      ...program,
      layers: modifiedMediaLayers
    };
    broadcast = { ...broadcast, program: updatedProgram };
  }

  return updateExpiredBackgroundImage({
    ...broadcast,
    overlays: modifiedOverlays,
    backgrounds: modifiedBackgrounds,
    globalSceneLayers: modifiedGlobalSceneLayers
  });
};

/**
 * Get an object value even when the value is in "legacy" format for media sources (used to be just the event doc ID)
 * @param {*} value
 */
export const getMediaSourceValueObject = value => {
  return typeof value === 'string'
    ? {
        eventDocId: value
      }
    : value;
};

export const mapSignedUrlsToSources = async (
  items,
  apiClient,
  team,
  event,
  isS3ProxyStatusUpdate = false
) => {
  if (items == null || items.length === 0) {
    return [];
  }

  const minutes = getMinutesDifference(
    signedURLsCache.lastUpdated,
    new Date().getTime()
  );
  const isValidCache = minutes < SIGNED_URLS_CACHE_TIMEOUT_VALUE;

  const eventDocIds = items.reduce((acc, item) => {
    if (item === 'SCENE_LAYER') {
      return acc;
    }

    const { value, type } = item.source;
    if (!s3SourceTypes.includes(type) || value.type === STOCK_MEDIA) {
      return acc;
    }

    const objectValue = getMediaSourceValueObject(value);

    if (
      signedURLsCache[objectValue.eventDocId] &&
      isValidCache &&
      !isS3ProxyStatusUpdate
    ) {
      return acc;
    }

    acc.push(objectValue.eventDocId);
    return acc;
  }, []);

  /**
   * We won't make an API call when eventDocIds are empty.
   * Ensure signed URLs cache is not outdated before using it.
   */
  if (!eventDocIds.length && isValidCache) {
    const response = getSignedUrlsResponse(items, signedURLsCache);
    return response;
  }

  try {
    const { urls } = await apiClient.eventDoc.getSignedUrls(team, event, {
      eventDocs: eventDocIds
    });
    // update signedURLCache including last updated timestamp.
    signedURLsCache = {
      ...signedURLsCache,
      ...urls,
      lastUpdated: new Date().getTime()
    };
    const response = getSignedUrlsResponse(items, signedURLsCache);
    return response;
  } catch (error) {
    // we probably want to dispatch an error handling action in here
    const message = `An error occurred calling eventDoc.getSignedUrls to team: ${team} event: ${event}`;

    logger.error(message, error);

    return [];
  }
};

const getSignedUrlsResponse = (items, signedUrls) => {
  const response = items.map(item => {
    if (item === 'SCENE_LAYER') {
      return item;
    }

    const { value, type } = item.source;

    if (!s3SourceTypes.includes(type) || value.type === STOCK_MEDIA) {
      return item;
    }

    const objectValue = getMediaSourceValueObject(value);
    return {
      ...item,
      layer: { ...item.layer },
      source: {
        ...item.source,
        value: {
          ...objectValue,
          url: signedUrls[objectValue.eventDocId]
        }
      }
    };
  });

  return response;
};

/**
 * Check the broadcast background layerSource objects to see if one of them matches the program style background Image.
 * If one is found, update it with the layer source url from the background layerSource, so that we'll be using a valid url
 * for the background style. If we don't do this, it's possible the program background image can become expired and no longer work
 * after a time.
 * @param {*} broadcast
 */
export const updateExpiredBackgroundImage = broadcast => {
  let existingBackgroundUrl = broadcast?.program?.style?.backgroundImage;

  if (!existingBackgroundUrl || !broadcast.backgrounds) return broadcast;

  const backgroundUrlResource = getUrlResource(existingBackgroundUrl);

  _.each(broadcast.backgrounds, bgLayerSource => {
    const layerSourceUrl = bgLayerSource.source.value.url;
    if (
      layerSourceUrl &&
      getUrlResource(layerSourceUrl) === backgroundUrlResource
    ) {
      broadcast.program.style.backgroundImage = `url(${bgLayerSource.source.value.url})`;
    }
  });

  return broadcast;
};

const getUrlResource = raw => {
  const matches = raw.match(/:\/\/(.[^/]+)(.*)/);
  const parsedUrl = new URL(`http://${matches[1]}${matches[2]}`);
  return parsedUrl.pathname;
};

/**
 * Extracts the role assigned to the current user.
 *
 * @param {Object} payload - payload object.
 * @param {string} eventId - the current event id.
 * @param {string} teamId - the current team id.
 * @param {file} -- File to be converted in binary format
 */
export async function handleDocumentUpload(
  apiClient,
  teamId,
  eventId,
  payload,
  file,
  startFileUploadActionCallback
) {
  payload.name = file.name;
  const createDocumentResponse = await apiClient.eventDoc.createDocument(
    teamId,
    eventId,
    payload
  );
  const {
    _id: eventDocId,
    preSignedUploadUrl,
    preSignedUploadHeaders
  } = createDocumentResponse;

  // Start file upload action tracker
  startFileUploadActionCallback?.({
    externalVideoId: createDocumentResponse.externalVideoId
  });

  const binaryData = await readFileAsBinary(file);
  const fileExtension = file.name.split('.').pop().toLowerCase();
  const contentType = fileTypes[fileExtension][0];
  const uploadHeaders =
    preSignedUploadHeaders &&
    preSignedUploadHeaders['x-amz-tagging']?.join(',');
  await uploadToPreSignedURL(
    preSignedUploadUrl,
    binaryData,
    contentType,
    uploadHeaders
  );

  return eventDocId;
}

// Attendee presenter management
export const AUTO_CREATED_FOR = 'auto-created for';
export const isAutoCreatedScreenSharePresenter = (attendeeId, title) =>
  attendeeId.includes('#content') && title.includes(AUTO_CREATED_FOR);
